import { useEffect, useState } from 'react'
import { api } from '../lib/api'

interface PlanCard {
  key: string
  name: string
  price: string
  period: string
  features: string[]
  highlight?: boolean
}

const PLANS: PlanCard[] = [
  {
    key: 'FREE',
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: [
      'Basic workout tracking',
      'Community leaderboard',
      'Daily quests',
    ],
  },
  {
    key: 'FIT',
    name: 'Fit',
    price: '$5',
    period: '/mo',
    highlight: true,
    features: [
      'All Free features',
      'Full workout programs',
      'Live bootcamp classes',
      'Progress tracking & photos',
      'Guild membership',
    ],
  },
  {
    key: 'ELITE',
    name: 'Elite',
    price: '$15',
    period: '/mo',
    features: [
      'All Fit features',
      'Priority class booking',
      '1-on-1 coach check-ins',
      'Custom workout plans',
      'Exclusive boss battles',
      'Elite badge & cosmetics',
    ],
  },
]

export default function BillingPage() {
  const [subscription, setSubscription] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)

  useEffect(() => {
    api.getSubscription()
      .then(({ subscription }) => setSubscription(subscription))
      .catch(() => setSubscription({ plan: 'FREE', status: 'ACTIVE' }))
      .finally(() => setLoading(false))
  }, [])

  const handleSubscribe = async (plan: string) => {
    if (plan === 'FREE') return
    setCheckoutLoading(plan)
    try {
      const { url } = await api.createCheckout(plan)
      if (url) window.location.href = url
    } catch (err: any) {
      alert(err.message || 'Failed to start checkout')
    } finally {
      setCheckoutLoading(null)
    }
  }

  const handleManage = async () => {
    setPortalLoading(true)
    try {
      const { url } = await api.createPortalSession()
      if (url) window.location.href = url
    } catch (err: any) {
      alert(err.message || 'Failed to open billing portal')
    } finally {
      setPortalLoading(false)
    }
  }

  const currentPlan = subscription?.plan || 'FREE'
  const currentStatus = subscription?.status || 'ACTIVE'

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <p className="font-pixel" style={{ color: 'var(--gold)', fontSize: '0.7rem' }}>Loading plans...</p>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <h2>Plans & Billing</h2>
        <p>Choose your path to greatness.</p>
      </div>

      {/* Current Subscription Status */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <span className="card-title">Current Plan</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div className="stat-card" style={{ flex: '1', minWidth: '140px' }}>
            <div className="stat-value" style={{ fontSize: '1.2rem' }}>
              {currentPlan === 'FREE' ? 'Free' : currentPlan === 'FIT' ? 'Fit' : currentPlan === 'ELITE' ? 'Elite' : 'Couples'}
            </div>
            <div className="stat-label">Plan</div>
          </div>
          <div className="stat-card" style={{ flex: '1', minWidth: '140px' }}>
            <div className="stat-value" style={{
              fontSize: '1rem',
              color: currentStatus === 'ACTIVE' ? 'var(--accent-green)' :
                     currentStatus === 'PAST_DUE' ? 'var(--accent-red)' :
                     'var(--text-secondary)',
            }}>
              {currentStatus}
            </div>
            <div className="stat-label">Status</div>
          </div>
          {currentPlan !== 'FREE' && subscription?.currentPeriodEnd && (
            <div className="stat-card" style={{ flex: '1', minWidth: '140px' }}>
              <div className="stat-value" style={{ fontSize: '0.85rem' }}>
                {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </div>
              <div className="stat-label">Renews</div>
            </div>
          )}
        </div>
        {currentPlan !== 'FREE' && (
          <div style={{ marginTop: '1rem' }}>
            <button
              className="btn-secondary"
              onClick={handleManage}
              disabled={portalLoading}
              style={{ fontSize: '0.75rem' }}
            >
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          </div>
        )}
      </div>

      {/* Plan Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        {PLANS.map((plan) => (
          <div
            key={plan.key}
            className="card"
            style={{
              border: plan.highlight ? '2px solid var(--gold)' : undefined,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {plan.highlight && (
              <div
                className="font-pixel"
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '-2rem',
                  background: 'var(--gold)',
                  color: 'var(--bg-dark)',
                  padding: '0.15rem 2.5rem',
                  fontSize: '0.45rem',
                  transform: 'rotate(45deg)',
                  fontWeight: 'bold',
                }}
              >
                POPULAR
              </div>
            )}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <h3 className="font-pixel" style={{ color: 'var(--gold)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                {plan.name}
              </h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                {plan.price}
                <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{plan.period}</span>
              </div>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem 0' }}>
              {plan.features.map((feature, i) => (
                <li key={i} style={{
                  padding: '0.35rem 0',
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)',
                  borderBottom: '1px solid var(--border-color)',
                }}>
                  <span style={{ color: 'var(--accent-green)', marginRight: '0.5rem' }}>+</span>
                  {feature}
                </li>
              ))}
            </ul>
            <div style={{ textAlign: 'center' }}>
              {currentPlan === plan.key ? (
                <button className="btn-secondary" disabled style={{ fontSize: '0.8rem', opacity: 0.6 }}>
                  Current Plan
                </button>
              ) : plan.key === 'FREE' ? (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Included by default</span>
              ) : (
                <button
                  className="btn-primary"
                  onClick={() => handleSubscribe(plan.key)}
                  disabled={checkoutLoading !== null}
                  style={{ fontSize: '0.8rem', width: '100%' }}
                >
                  {checkoutLoading === plan.key ? 'Loading...' : `Subscribe to ${plan.name}`}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Couples Plan */}
      <div className="card" style={{ border: '1px solid var(--accent-purple, #9b59b6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h3 className="font-pixel" style={{ color: 'var(--gold)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
              Couples Plan
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
              Train together for <strong style={{ color: 'var(--text-primary)' }}>$8/mo</strong> — two accounts, all Fit features.
            </p>
          </div>
          {currentPlan === 'COUPLES' ? (
            <button className="btn-secondary" disabled style={{ fontSize: '0.8rem', opacity: 0.6 }}>
              Current Plan
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={() => handleSubscribe('COUPLES')}
              disabled={checkoutLoading !== null}
              style={{ fontSize: '0.8rem' }}
            >
              {checkoutLoading === 'COUPLES' ? 'Loading...' : 'Get Couples Plan'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
